import { useEffect, useState } from "react";
import { adminApi, formatApiError } from "@/lib/api";
import { Check, X, Star, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const STATUSES = ["pending", "approved", "rejected"];

export default function ReviewsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  async function load() {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/reviews");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function updateStatus(id, status) {
    try {
      await adminApi.patch(`/admin/reviews/${id}`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function toggleFeatured(r) {
    try {
      await adminApi.patch(`/admin/reviews/${r.id}`, { featured: !r.featured });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }
  async function remove(r) {
    if (!window.confirm(`Delete review by ${r.name}?`)) return;
    try {
      await adminApi.delete(`/admin/reviews/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  const filtered = items.filter((r) => filter === "all" || r.status === filter);

  return (
    <div data-testid="reviews-manager" className="space-y-6">
      <div className="flex flex-wrap gap-2">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[var(--trio-muted)] font-bold self-center mr-1">
          Filter:
        </span>
        {["all", ...STATUSES].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-testid={`review-filter-${s}`}
            className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
              filter === s
                ? "bg-[var(--trio-text)] text-[var(--trio-bg)] border-[var(--trio-text)]"
                : "border-[var(--trio-border)] hover:border-[var(--trio-text)]"
            }`}
          >
            {s} {s !== "all" && `(${items.filter((r) => r.status === s).length})`}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-[var(--trio-muted)]">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-sm text-[var(--trio-muted)] py-12 text-center bg-[var(--trio-surface)]">
          No reviews here.
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <article
              key={r.id}
              data-testid={`review-row-${r.id}`}
              className="bg-[var(--trio-surface)] border border-[var(--trio-border)] p-5 grid grid-cols-1 md:grid-cols-12 gap-4 items-start"
            >
              <div className="md:col-span-3">
                <div className="font-display uppercase text-lg tracking-tight">
                  {r.name}
                </div>
                <div className="flex gap-0.5 mt-1">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star
                      key={s}
                      className={`w-3.5 h-3.5 ${
                        s < r.rating
                          ? "fill-[var(--trio-accent)] text-[var(--trio-accent)]"
                          : "text-[var(--trio-border)]"
                      }`}
                    />
                  ))}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[var(--trio-muted)] mt-2">
                  {new Date(r.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="md:col-span-6 text-sm text-[var(--trio-muted)]">
                “{r.message}”
              </div>
              <div className="md:col-span-3 flex flex-col gap-2 items-end">
                <StatusBadge status={r.status} />
                {r.status === "approved" && (
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <Switch
                      checked={r.featured}
                      onCheckedChange={() => toggleFeatured(r)}
                      data-testid={`review-featured-${r.id}`}
                    />
                    <span className="text-[10px] uppercase tracking-widest">
                      Featured
                    </span>
                  </label>
                )}
                <div className="flex gap-1">
                  <IconBtn
                    title="Approve"
                    testid={`review-approve-${r.id}`}
                    onClick={() => updateStatus(r.id, "approved")}
                  >
                    <Check className="w-4 h-4 text-emerald-700" />
                  </IconBtn>
                  <IconBtn
                    title="Reject"
                    testid={`review-reject-${r.id}`}
                    onClick={() => updateStatus(r.id, "rejected")}
                  >
                    <X className="w-4 h-4 text-red-700" />
                  </IconBtn>
                  <IconBtn
                    title="Delete"
                    testid={`review-delete-${r.id}`}
                    onClick={() => remove(r)}
                  >
                    <Trash2 className="w-4 h-4 text-red-700" />
                  </IconBtn>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: "bg-amber-100 text-amber-900 border-amber-300",
    approved: "bg-emerald-100 text-emerald-900 border-emerald-300",
    rejected: "bg-red-100 text-red-900 border-red-300",
  };
  return (
    <span
      className={`text-[10px] tracking-[0.25em] uppercase font-bold border px-2 py-1 ${
        styles[status] || ""
      }`}
    >
      {status}
    </span>
  );
}

function IconBtn({ children, onClick, title, testid }) {
  return (
    <button
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="p-2 border border-[var(--trio-border)] hover:bg-[var(--trio-bg)] transition-colors"
    >
      {children}
    </button>
  );
}
